import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Alert,
  Snackbar,
  TextField,
  IconButton,
  Link,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import DataTable, { Column } from '../../components/common/DataTable';
import SearchBar from '../../components/common/SearchBar';
import StatusChip from '../../components/common/StatusChip';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import {
  getPhoneReports,
  approvePhoneReport,
  rejectPhoneReport,
  updatePhoneReport,
  deletePhoneReport,
} from '../../services/api/reportApi';
import { PhoneReport, ReportFilters } from '../../types/report';
import { formatDateTime } from '../../utils/formatters';

const reportTypeLabels: Record<string, string> = {
  police: '경찰',
  prosecutor: '검찰',
  bank: '은행',
  government: '정부기관',
  other: '기타',
  not_spam: '스팸 아님',
};

const PhoneReportListPage: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [filters, setFilters] = useState<ReportFilters>({
    search: '',
    page: 0,
    limit: 20,
  });
  const [searchInput, setSearchInput] = useState('');
  const [selectedReport, setSelectedReport] = useState<PhoneReport | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    type: 'approve' | 'reject' | 'delete' | null;
    reportId: string | null;
  }>({
    open: false,
    type: null,
    reportId: null,
  });
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [displayLabelInput, setDisplayLabelInput] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState<{
    phoneNumber: string;
    reportType: 'police' | 'prosecutor' | 'bank' | 'government' | 'other' | 'not_spam';
    description: string;
    displayLabel: string;
  }>({
    phoneNumber: '',
    reportType: 'police',
    description: '',
    displayLabel: '',
  });
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const { data, isLoading } = useQuery({
    queryKey: ['phoneReports', filters],
    queryFn: () => getPhoneReports(filters),
  });

  const approveMutation = useMutation({
    mutationFn: ({ id, displayLabel }: { id: string; displayLabel?: string }) => 
      approvePhoneReport(id, displayLabel),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['phoneReports'] });
      setSnackbar({
        open: true,
        message: '신고가 승인되었습니다. 스팸 번호 관리에서 확인할 수 있습니다.',
        severity: 'success',
      });
      setDetailDialogOpen(false);
      setApproveDialogOpen(false);
      setDisplayLabelInput('');
      setConfirmDialog({ open: false, type: null, reportId: null });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.message || '신고 승인에 실패했습니다.',
        severity: 'error',
      });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: (id: string) => rejectPhoneReport(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['phoneReports'] });
      setSnackbar({
        open: true,
        message: '신고가 거부되었습니다.',
        severity: 'success',
      });
      setDetailDialogOpen(false);
      setConfirmDialog({ open: false, type: null, reportId: null });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.message || '신고 거부에 실패했습니다.',
        severity: 'error',
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<PhoneReport> }) =>
      updatePhoneReport(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['phoneReports'] });
      setSnackbar({
        open: true,
        message: '신고가 수정되었습니다.',
        severity: 'success',
      });
      setEditDialogOpen(false);
      setDetailDialogOpen(false);
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.message || '신고 수정에 실패했습니다.',
        severity: 'error',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deletePhoneReport,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['phoneReports'] });
      setSnackbar({
        open: true,
        message: '신고가 삭제되었습니다.',
        severity: 'success',
      });
      setDetailDialogOpen(false);
      setConfirmDialog({ open: false, type: null, reportId: null });
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.message || '신고 삭제에 실패했습니다.',
        severity: 'error',
      });
    },
  });

  const handleSearch = () => {
    setFilters(prev => ({ ...prev, search: searchInput, page: 0 }));
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleRowsPerPageChange = (limit: number) => {
    setFilters(prev => ({ ...prev, limit, page: 0 }));
  };

  const handleStatusFilterChange = (value: string) => {
    setFilters(prev => ({
      ...prev,
      status: value === 'all' ? undefined : (value as 'pending' | 'approved' | 'rejected'),
      page: 0,
    }));
  };

  const handleReportTypeFilterChange = (value: string) => {
    setFilters(prev => ({
      ...prev,
      reportType: value === 'all' ? undefined : value,
      page: 0,
    }));
  };

  const handleRowClick = (report: PhoneReport) => {
    setSelectedReport(report);
    setDetailDialogOpen(true);
  };

  const handleCloseDetailDialog = () => {
    setDetailDialogOpen(false);
    setSelectedReport(null);
  };

  const handleApproveClick = (reportId: string) => {
    setConfirmDialog({
      open: false,
      type: 'approve',
      reportId,
    });
    setDisplayLabelInput('');
    setApproveDialogOpen(true);
  };

  const handleApproveConfirm = () => {
    if (!confirmDialog.reportId) return;
    approveMutation.mutate({ 
      id: confirmDialog.reportId, 
      displayLabel: displayLabelInput.trim() || undefined 
    });
  };

  const handleRejectClick = (reportId: string) => {
    setConfirmDialog({
      open: true,
      type: 'reject',
      reportId,
    });
  };

  const handleEditClick = () => {
    if (!selectedReport) return;
    setEditFormData({
      phoneNumber: selectedReport.phoneNumber,
      reportType: selectedReport.reportType,
      description: selectedReport.description || '',
      displayLabel: selectedReport.displayLabel || '',
    });
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (reportId: string) => {
    setConfirmDialog({
      open: true,
      type: 'delete',
      reportId,
    });
  };

  const handleEditFormChange = (
    field: keyof typeof editFormData,
    value: string | 'police' | 'prosecutor' | 'bank' | 'government' | 'other' | 'not_spam'
  ) => {
    setEditFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveEdit = () => {
    if (!selectedReport) return;
    updateMutation.mutate({
      id: selectedReport.id,
      data: {
        phoneNumber: editFormData.phoneNumber,
        reportType: editFormData.reportType,
        description: editFormData.description,
        displayLabel: editFormData.displayLabel || undefined,
      } as any,
    });
  };

  const handleConfirmAction = () => {
    if (!confirmDialog.reportId) return;

    if (confirmDialog.type === 'reject') {
      rejectMutation.mutate(confirmDialog.reportId);
    } else if (confirmDialog.type === 'delete') {
      deleteMutation.mutate(confirmDialog.reportId);
    }
  };

  const handleCancelAction = () => {
    setConfirmDialog({ open: false, type: null, reportId: null });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const columns: Column<PhoneReport>[] = [
    {
      id: 'phoneNumber',
      label: '전화번호',
      minWidth: 150,
      sortable: true,
    },
    {
      id: 'reportType',
      label: '신고 유형',
      minWidth: 120,
      format: (value: string) => reportTypeLabels[value] || value,
    },
    {
      id: 'reporter',
      label: '신고자',
      minWidth: 150,
      format: (_value: any, row: PhoneReport) => row.reporter.username,
    },
    {
      id: 'status',
      label: '상태',
      minWidth: 100,
      align: 'center',
      format: (value: string) => {
        const statusLabels: Record<string, string> = {
          pending: '대기',
          approved: '승인',
          rejected: '거부',
        };
        return <StatusChip status={value} label={statusLabels[value] || value} />;
      },
    },
    {
      id: 'createdAt',
      label: '신고일',
      minWidth: 150,
      format: (value: Date) => formatDateTime(value),
      sortable: true,
    },
    {
      id: 'approvedAt',
      label: '처리일',
      minWidth: 150,
      format: (value: Date | null) => (value ? formatDateTime(value) : '-'),
    },
  ];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        전화번호 신고 관리
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{ flex: 1 }}>
            <SearchBar
              value={searchInput}
              onChange={setSearchInput}
              onSearch={handleSearch}
              placeholder="전화번호로 검색"
              fullWidth
            />
          </Box>
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>상태 필터</InputLabel>
            <Select
              value={filters.status || 'all'}
              label="상태 필터"
              onChange={e => handleStatusFilterChange(e.target.value)}
              size="small"
            >
              <MenuItem value="all">전체</MenuItem>
              <MenuItem value="pending">대기</MenuItem>
              <MenuItem value="approved">승인</MenuItem>
              <MenuItem value="rejected">거부</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>유형 필터</InputLabel>
            <Select
              value={filters.reportType || 'all'}
              label="유형 필터"
              onChange={e => handleReportTypeFilterChange(e.target.value)}
              size="small"
            >
              <MenuItem value="all">전체</MenuItem>
              <MenuItem value="police">경찰</MenuItem>
              <MenuItem value="prosecutor">검찰</MenuItem>
              <MenuItem value="bank">은행</MenuItem>
              <MenuItem value="government">정부기관</MenuItem>
              <MenuItem value="other">기타</MenuItem>
              <MenuItem value="not_spam">스팸 아님</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      <DataTable
        columns={columns}
        data={data?.items || []}
        total={data?.total || 0}
        page={filters.page}
        rowsPerPage={filters.limit}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        onRowClick={handleRowClick}
        loading={isLoading}
      />

      {/* Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={handleCloseDetailDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>신고 상세 정보</DialogTitle>
        <DialogContent>
          {selectedReport && (
            <Box sx={{ pt: 2 }}>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      전화번호
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedReport.phoneNumber}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      신고 유형
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {reportTypeLabels[selectedReport.reportType] || selectedReport.reportType}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      신고자
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedReport.reporter.username} ({selectedReport.reporter.email})
                    </Typography>
                  </Box>
                  <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      상태
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <StatusChip
                        status={selectedReport.status}
                        label={
                          selectedReport.status === 'pending'
                            ? '대기'
                            : selectedReport.status === 'approved'
                            ? '승인'
                            : '거부'
                        }
                      />
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      신고일
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {formatDateTime(selectedReport.createdAt)}
                    </Typography>
                  </Box>
                  {selectedReport.approvedAt && (
                    <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        처리일
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {formatDateTime(selectedReport.approvedAt)}
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Divider />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    신고 내용
                  </Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mt: 1 }}>
                    {selectedReport.description || '내용 없음'}
                  </Typography>
                </Box>
                {selectedReport.displayLabel && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      오버레이 표시 텍스트
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 1 }}>
                      {selectedReport.displayLabel}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Box sx={{ display: 'flex', gap: 1, width: '100%', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton
                onClick={handleEditClick}
                color="primary"
                size="small"
                title="수정"
              >
                <EditIcon />
              </IconButton>
              <IconButton
                onClick={() => selectedReport && handleDeleteClick(selectedReport.id)}
                color="error"
                size="small"
                title="삭제"
              >
                <DeleteIcon />
              </IconButton>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {selectedReport?.status === 'pending' && (
                <>
                  <Button
                    onClick={() => handleRejectClick(selectedReport.id)}
                    color="error"
                    variant="outlined"
                  >
                    거부
                  </Button>
                  <Button
                    onClick={() => handleApproveClick(selectedReport.id)}
                    color="success"
                    variant="contained"
                  >
                    승인
                  </Button>
                </>
              )}
              <Button onClick={handleCloseDetailDialog}>닫기</Button>
            </Box>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>신고 수정</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              label="전화번호"
              value={editFormData.phoneNumber}
              onChange={e => handleEditFormChange('phoneNumber', e.target.value)}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>신고 유형</InputLabel>
              <Select
                value={editFormData.reportType}
                label="신고 유형"
                onChange={e => handleEditFormChange('reportType', e.target.value)}
              >
                <MenuItem value="police">경찰</MenuItem>
                <MenuItem value="prosecutor">검찰</MenuItem>
                <MenuItem value="bank">은행</MenuItem>
                <MenuItem value="government">정부기관</MenuItem>
                <MenuItem value="other">기타</MenuItem>
                <MenuItem value="not_spam">스팸 아님</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="신고 내용"
              value={editFormData.description}
              onChange={e => handleEditFormChange('description', e.target.value)}
              multiline
              rows={4}
              fullWidth
            />
            {editFormData.reportType === 'not_spam' && (
              <TextField
                label="오버레이 표시 텍스트"
                value={editFormData.displayLabel}
                onChange={e => handleEditFormChange('displayLabel', e.target.value)}
                placeholder="예: 배달의민족, 카카오택시, 병원 예약"
                helperText="전화 수신 시 오버레이에 표시될 텍스트입니다."
                fullWidth
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>취소</Button>
          <Button onClick={handleSaveEdit} variant="contained" color="primary">
            저장
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approve Dialog with Display Label */}
      <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>신고 승인</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Alert severity="info">
              승인하면 해당 번호가 <strong>스팸 번호 관리</strong>에 자동으로 반영됩니다.{' '}
              <Link
                component="button"
                variant="body2"
                onClick={() => { setApproveDialogOpen(false); navigate('/security/spam-phones'); }}
                sx={{ cursor: 'pointer', verticalAlign: 'baseline' }}
              >
                스팸 번호 관리 바로가기 <OpenInNewIcon sx={{ fontSize: 12, verticalAlign: 'middle' }} />
              </Link>
            </Alert>
            <TextField
              label="오버레이 표시 텍스트 (선택)"
              value={displayLabelInput}
              onChange={e => setDisplayLabelInput(e.target.value)}
              placeholder="예: 보이스피싱, 대출 사기"
              helperText="전화 수신 시 오버레이에 표시될 텍스트입니다."
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialogOpen(false)}>취소</Button>
          <Button onClick={handleApproveConfirm} variant="contained" color="success">
            승인
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        title={
          confirmDialog.type === 'approve'
            ? '신고 승인'
            : confirmDialog.type === 'reject'
            ? '신고 거부'
            : '신고 삭제'
        }
        message={
          confirmDialog.type === 'approve'
            ? '이 신고를 승인하시겠습니까?'
            : confirmDialog.type === 'reject'
            ? '이 신고를 거부하시겠습니까?'
            : '이 신고를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.'
        }
        onConfirm={handleConfirmAction}
        onCancel={handleCancelAction}
        confirmText={
          confirmDialog.type === 'approve' ? '승인' : confirmDialog.type === 'reject' ? '거부' : '삭제'
        }
        confirmColor={confirmDialog.type === 'approve' ? 'success' : 'error'}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
          {snackbar.severity === 'success' && snackbar.message.includes('스팸 번호 관리') && (
            <Link
              component="button"
              variant="body2"
              onClick={() => { handleCloseSnackbar(); navigate('/security/spam-phones'); }}
              sx={{ ml: 1, cursor: 'pointer', color: 'inherit', fontWeight: 'bold' }}
            >
              바로가기
            </Link>
          )}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PhoneReportListPage;
